import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Trophy, Crown, Medal, Loader2 } from "lucide-react";

export default function Leaderboard() {
  const [scope, setScope] = useState("agency");
  const [data, setData] = useState(null);

  const load = (sc) => api.get(`/leaderboard?scope=${sc}`).then((r) => setData(r.data));
  useEffect(() => { load(scope); }, [scope]);

  const podium = data?.entries?.slice(0, 3) || [];
  const rest = data?.entries?.slice(3) || [];

  const podiumStyle = [
    { order: "order-2", h: "h-32", ring: "from-yellow-400 to-brand-orange", icon: <Crown size={20} className="text-yellow-400" /> },
    { order: "order-1 sm:mt-8", h: "h-24", ring: "from-slate-300 to-slate-500", icon: <Medal size={18} className="text-slate-300" /> },
    { order: "order-3 sm:mt-12", h: "h-20", ring: "from-amber-600 to-amber-800", icon: <Medal size={18} className="text-amber-600" /> },
  ];
  const podiumArr = podium.length === 3 ? [podium[0], podium[1], podium[2]] : podium;

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
            <Trophy className="text-brand-orange" /> Ranking
          </h1>
          <p className="text-brand-muted text-sm mt-1">Cooperação na competição — reconhece o esforço de toda a equipa.</p>
        </div>
        <div className="inline-flex rounded-sm border border-brand-border overflow-hidden" data-testid="leaderboard-scope">
          {[["agency", "A minha agência"], ["global", "Global"]].map(([v, l]) => (
            <button key={v} onClick={() => setScope(v)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${scope === v ? "bg-brand-blue text-white" : "text-brand-muted hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>
      ) : (
        <>
          {/* Podium */}
          {podium.length === 3 && (
            <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl mx-auto items-end py-6">
              {[1, 0, 2].map((idx, col) => {
                const e = podiumArr[idx];
                const st = podiumStyle[idx];
                if (!e) return <div key={col} />;
                return (
                  <div key={e.id} className={`${st.order} flex flex-col items-center`}>
                    <div className={`h-14 w-14 rounded-sm bg-gradient-to-br ${st.ring} flex items-center justify-center text-white font-heading font-black text-xl mb-2 glow-orange`}>
                      {e.name[0]}
                    </div>
                    <p className="text-sm font-semibold text-center truncate max-w-full">{e.name}{e.is_me && " (tu)"}</p>
                    <p className="text-xs text-brand-orange font-bold">{e.points} pts</p>
                    <div className={`${st.h} w-full mt-2 rounded-t-sm bg-brand-surface border border-brand-border flex items-start justify-center pt-2`}>
                      {st.icon}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Table */}
          <div className="rounded-sm bg-brand-surface border border-brand-border overflow-hidden">
            {(podium.length === 3 ? rest : data.entries).map((e, i) => (
              <motion.div key={e.id} layout data-testid="leaderboard-row"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className={`flex items-center gap-4 px-4 py-3 border-b border-brand-border last:border-0 ${e.is_me ? "bg-brand-blue/10" : ""}`}>
                <span className="w-6 text-center font-heading font-bold text-brand-muted">{e.rank}</span>
                <div className="h-9 w-9 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center text-sm font-bold text-white shrink-0">
                  {e.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{e.name}{e.is_me && <span className="text-brand-blue"> (tu)</span>}</p>
                  <p className="text-xs text-brand-muted">{e.level_name} · Nível {e.level}</p>
                </div>
                <span className="font-heading font-bold text-brand-orange">{e.points}</span>
              </motion.div>
            ))}
            {data.entries.length === 0 && <p className="p-8 text-center text-brand-muted">Sem participantes.</p>}
          </div>
        </>
      )}
    </div>
  );
}
